import { getAccessToken } from "@/shared/lib/token/token"

/**
 * Server-sent events over `fetch`.
 *
 * `EventSource` — and the polyfill wrapped in `event-source.ts` — can only issue GET requests, so
 * the request body would have to become a query string. That is not an option here: a prompt runs
 * to tens of thousands of characters, and Sentry records URLs with `sendDefaultPii` and session
 * replay on, so the text would land in telemetry. `fetch` with a streamed body avoids both.
 *
 * A POST unless told otherwise. A GET is for attaching to something the server is already doing —
 * an evaluation started earlier, from this tab or another — and carries nothing.
 *
 * The token-refresh caveat that applies to the polyfill applies here too — this request does not
 * pass through the axios interceptors, so nothing renews an expired access token on its behalf.
 * Callers make an axios request first, which is the same thing `translation.queries.ts` does and
 * documents.
 */
/**
 * A failed response, with whatever the server said about it.
 *
 * `message` is absent when the body held nothing usable — a proxy's HTML error page, an empty 502.
 * Callers must fall back rather than render an empty string.
 */
/**
 * A refusal the service described in a form this app can write itself.
 *
 * `code` names the rule that refused, `params` are what that rule measured. Both are absent for
 * every refusal the service has not yet been taught to code, which is most of them — the caller
 * falls back to `serverMessage` exactly as before.
 */
export type ServerRefusal = {
  code: string
  params: Record<string, unknown>
}

export class StreamResponseError extends Error {
  constructor(
    readonly status: number,
    readonly serverMessage?: string,
    readonly refusal?: ServerRefusal
  ) {
    super(serverMessage ?? `stream failed: ${status}`)
    this.name = "StreamResponseError"
  }
}

/**
 * The service answers `{"error": "..."}`, and for refusals it has been taught to code, also
 * `{"code": "...", "params": {...}}`. Anything else — HTML from a proxy, an empty body, a shape
 * that changed — yields nothing, and the caller shows its own wording.
 *
 * The code is read separately from the sentence rather than instead of it: a catalogue that has no
 * entry for a code yet must still be able to show what the service said.
 */
const readErrorBody = async (response: Response) => {
  try {
    const body = await response.json()
    const message = body?.error
    const code = body?.code
    const params = body?.params
    return {
      message: typeof message === "string" && message.trim() ? message : undefined,
      refusal:
        typeof code === "string" && code.trim()
          ? { code, params: params && typeof params === "object" ? (params as Record<string, unknown>) : {} }
          : undefined,
    }
  } catch {
    return { message: undefined, refusal: undefined }
  }
}

export const streamServerSentEvents = async <T>({
  url,
  method = "POST",
  body,
  signal,
  onEvent,
  onRequestSent,
}: {
  url: string
  /** `POST` unless said otherwise. A `GET` carries no body. */
  method?: "GET" | "POST"
  /** A FormData body is sent as-is; anything else is JSON-encoded. Not read on a GET. */
  body?: FormData | unknown
  signal?: AbortSignal
  onEvent: (event: T) => void
  /** Called when the request is about to go out with a live signal — that is, when the server may
   * receive it. Deliberately the dispatch rather than the response: a POST can be transmitted and
   * stored long before headers come back, and a caller deciding whether an abort left anything
   * behind has to assume it did from this point on. */
  onRequestSent?: () => void
}) => {
  const isFormData = body instanceof FormData
  // A GET has nothing to carry, and `fetch` rejects one that tries.
  const carriesBody = method !== "GET"

  // Before the call, and only if nothing has aborted yet. An already-aborted signal makes fetch
  // reject without transmitting, and reporting that as sent would lose the caller's draft for a
  // turn the server never saw.
  if (!signal?.aborted) onRequestSent?.()

  const response = await fetch(url, {
    method,
    headers: {
      // Deliberately absent for FormData. multipart/form-data carries a generated boundary in the
      // header, and setting the type by hand omits it — the server then reads zero fields from a
      // body that looks perfectly fine on the wire.
      ...(carriesBody && !isFormData ? { "Content-Type": "application/json" } : {}),
      Authorization: `Bearer ${getAccessToken()}`,
    },
    ...(carriesBody ? { body: isFormData ? body : JSON.stringify(body) } : {}),
    signal,
  })

  if (!response.ok || !response.body) {
    // The body is read, not discarded. A 4xx from this endpoint is a sentence about what the
    // caller sent — which file was too large, which limit it met — and throwing only the status
    // turned every one of them into the same "try again" the caller cannot act on. A deterministic
    // refusal read as a transient one, and the retry cost another upload.
    const { message, refusal } = await readErrorBody(response)
    throw new StreamResponseError(response.status, message, refusal)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  // `stream: true` on the decoder matters: a multi-byte character can straddle two chunks, and
  // decoding each independently would corrupt it. Korean answers hit this constantly.
  //
  // This tracks the other kind of straddling — a CRLF whose halves land in different reads.
  let previousEndedWithCarriageReturn = false

  const emit = (frame: string) => {
    const data = frame
      .split(/\r\n|\r|\n/)
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trimStart())
      .join("\n")
    if (!data) return

    try {
      onEvent(JSON.parse(data) as T)
    } catch {
      // A frame that will not parse is one event lost, not a reason to abandon a half-finished
      // answer.
    }
  }

  try {
    for (;;) {
      const { done, value } = await reader.read()

      if (done) {
        // Flush whatever the decoder is still holding, then the buffer. A server may close
        // without a trailing blank line — that is legal, and the last frame is usually the one
        // that says the turn was saved, so dropping it loses exactly the event that matters.
        buffer += decoder.decode()
        if (buffer.trim()) emit(buffer)
        break
      }

      // Frames are separated by a blank line. Anything after the last separator is a partial
      // frame and stays in the buffer for the next read.
      //
      // Line endings are normalised rather than matched shape by shape: SSE lets every line end
      // independently, so a producer or proxy can emit \r\n\n or \n\r\n between events and
      // enumerating delimiters catches only the homogeneous cases.
      //
      // A CRLF can straddle two reads, and the halves must still count as one line ending. That is
      // remembered rather than withheld: holding the CR back would delay a complete \r\r
      // delimiter — valid, and used by producers that flush per event — until more bytes arrived,
      // leaving the answer a full event behind or stalled outright on a quiet stream.
      let chunk = decoder.decode(value, { stream: true })
      if (previousEndedWithCarriageReturn && chunk.startsWith("\n")) {
        chunk = chunk.slice(1)
      }
      previousEndedWithCarriageReturn = chunk.endsWith("\r")

      buffer = (buffer + chunk).replace(/\r\n|\r/g, "\n")
      const frames = buffer.split("\n\n")
      buffer = frames.pop() ?? ""
      frames.forEach(emit)
    }
  } finally {
    // Releasing matters on the abort path: without it the connection is held until GC runs, and a
    // user who sends, cancels and re-sends stacks up sockets.
    reader.releaseLock()
  }
}
